import {
  CognitoError,
  cognitoRequest,
  tokensFrom,
  type CognitoTokens,
} from '@fatigue/shared';

/**
 * Passkeys, through Cognito's WebAuthn support.
 *
 * Cognito holds the passkeys and runs the ceremony; the browser's
 * `navigator.credentials` does the part with the person's fingerprint, face or
 * device PIN. What this file does is translate between the two: Cognito speaks
 * WebAuthn's JSON form, with every binary field base64url-encoded, and the
 * browser API wants ArrayBuffers in and gives ArrayBuffers back.
 *
 * Passkeys are bound to `app.thefatigueapp.com` (the pool's relying-party id,
 * `infra/stacks/auth_stack.py`), so they work on the deployed dashboard and
 * nowhere else — not on localhost, and not against the local IdP.
 */

/** Thrown when the address has no passkey to sign in with. */
export class NoPasskeyError extends Error {}

export interface Passkey {
  readonly id: string;
  /** What the browser or password manager called it, e.g. "iCloud Keychain". */
  readonly name: string;
  readonly createdAt: Date | null;
}

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
}

/* ------------------------------------------------------------ encodings */

function fromBase64Url(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function toBase64Url(buffer: ArrayBuffer | null): string | undefined {
  if (buffer === null) return undefined;
  let binary = '';
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

type Json = Record<string, unknown>;

function withIds(list: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(list)) return undefined;
  return list.map((entry: Json) => ({
    ...(entry as unknown as PublicKeyCredentialDescriptor),
    id: fromBase64Url(String(entry['id'])),
  }));
}

function creationOptions(json: Json): PublicKeyCredentialCreationOptions {
  const user = json['user'] as Json;
  return {
    ...(json as unknown as PublicKeyCredentialCreationOptions),
    challenge: fromBase64Url(String(json['challenge'])),
    user: { ...(user as unknown as PublicKeyCredentialUserEntity), id: fromBase64Url(String(user['id'])) },
    excludeCredentials: withIds(json['excludeCredentials']) ?? [],
  };
}

function requestOptions(json: Json): PublicKeyCredentialRequestOptions {
  return {
    ...(json as unknown as PublicKeyCredentialRequestOptions),
    challenge: fromBase64Url(String(json['challenge'])),
    allowCredentials: withIds(json['allowCredentials']) ?? [],
  };
}

/** The browser's credential, in the JSON form Cognito expects back. */
function credentialJson(credential: PublicKeyCredential): Json {
  const response = credential.response as AuthenticatorAttestationResponse &
    AuthenticatorAssertionResponse;
  const transports =
    typeof response.getTransports === 'function' ? response.getTransports() : undefined;
  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: toBase64Url(response.clientDataJSON),
      // Registration.
      attestationObject:
        'attestationObject' in response ? toBase64Url(response.attestationObject) : undefined,
      transports,
      // Sign-in.
      authenticatorData:
        'authenticatorData' in response ? toBase64Url(response.authenticatorData) : undefined,
      signature: 'signature' in response ? toBase64Url(response.signature) : undefined,
      userHandle: 'userHandle' in response ? toBase64Url(response.userHandle) : undefined,
    },
  };
}

/* -------------------------------------------------------------- sign-in */

export async function passkeySignIn(clientId: string, email: string): Promise<CognitoTokens> {
  const started = await cognitoRequest('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: clientId,
    AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: 'WEB_AUTHN' },
  });

  const parameters = (started['ChallengeParameters'] ?? {}) as Json;
  const options = parameters['CREDENTIAL_REQUEST_OPTIONS'];
  if (started['ChallengeName'] !== 'WEB_AUTHN' || typeof options !== 'string') {
    // Cognito offers another way in when the address has no passkey.
    throw new NoPasskeyError();
  }

  const credential = (await navigator.credentials.get({
    publicKey: requestOptions(JSON.parse(options) as Json),
  })) as PublicKeyCredential | null;
  if (credential === null) throw new NoPasskeyError();

  return tokensFrom(
    await cognitoRequest('RespondToAuthChallenge', {
      ChallengeName: 'WEB_AUTHN',
      ClientId: clientId,
      Session: started['Session'],
      ChallengeResponses: {
        USERNAME: email,
        CREDENTIAL: JSON.stringify(credentialJson(credential)),
      },
    }),
  );
}

/* ----------------------------------------------------------- management */

export async function listPasskeys(accessToken: string): Promise<readonly Passkey[]> {
  const response = await cognitoRequest('ListWebAuthnCredentials', { AccessToken: accessToken });
  const credentials = Array.isArray(response['Credentials']) ? (response['Credentials'] as Json[]) : [];
  return credentials.map((c) => ({
    id: String(c['CredentialId']),
    name: typeof c['FriendlyCredentialName'] === 'string' ? c['FriendlyCredentialName'] : 'Passkey',
    // Cognito returns epoch seconds.
    createdAt: typeof c['CreatedAt'] === 'number' ? new Date(c['CreatedAt'] * 1000) : null,
  }));
}

export async function addPasskey(accessToken: string): Promise<void> {
  const started = await cognitoRequest('StartWebAuthnRegistration', { AccessToken: accessToken });
  const options = started['CredentialCreationOptions'];
  if (typeof options !== 'object' || options === null) {
    throw new CognitoError('UnusableResponse', 'Passkeys are not available right now.');
  }

  const credential = (await navigator.credentials.create({
    publicKey: creationOptions(options as Json),
  })) as PublicKeyCredential | null;
  if (credential === null) throw new CognitoError('Cancelled', 'No passkey was created.');

  await cognitoRequest('CompleteWebAuthnRegistration', {
    AccessToken: accessToken,
    Credential: credentialJson(credential),
  });
}

export async function removePasskey(accessToken: string, id: string): Promise<void> {
  await cognitoRequest('DeleteWebAuthnCredential', { AccessToken: accessToken, CredentialId: id });
}
